/**
 * Support Tab
 * Admin interface for managing contact form submissions
 */

import { LoadingSpinner } from '@components/ui';
import type { SubmissionListItem } from '@hooks/useContactSubmissions';
import { useContactSubmissions } from '@hooks/useContactSubmissions';
import { useSubmissionActions } from '@hooks/useSubmissionActions';
import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Mail,
  MessageSquare,
  Search,
  Send,
  User,
  Users,
} from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface SubmissionDetailModalProps {
  submission: SubmissionListItem;
  onClose: () => void;
  onUpdate: () => void;
}

// Simple detail modal (can be expanded later)
function SubmissionDetailModal({
  submission,
  onClose,
  onUpdate,
}: SubmissionDetailModalProps): ReactElement {
  const { updateStatus, loading } = useSubmissionActions();
  const [selectedStatus, setSelectedStatus] = useState(submission.status);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleStatusChange = async (newStatus: 'new' | 'in_progress' | 'resolved') => {
    setActionError(null);
    setActionSuccess(null);

    const result = await updateStatus(submission.id, newStatus);
    if (result.success) {
      setSelectedStatus(newStatus);
      setActionSuccess('Status updated successfully');
      // Refresh the list after a short delay
      setTimeout(() => {
        onUpdate();
      }, 500);
    } else {
      setActionError(result.error || 'Failed to update status');
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'new':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'in_progress':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'resolved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-theme-surface rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-6">
          <h2 className="text-xl font-bold text-theme-text">Submission Details</h2>

          {/* Success/Error Messages */}
          {actionSuccess && (
            <div className="p-3 bg-theme-success/10 border border-theme-success rounded-lg text-theme-success flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">{actionSuccess}</span>
            </div>
          )}

          {actionError && (
            <div className="p-3 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{actionError}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <span className="text-sm text-theme-text-secondary">From:</span>
              <p className="text-theme-text">
                {submission.name} ({submission.email})
              </p>
              {submission.isRegisteredUser && (
                <span className="text-xs text-theme-primary">Registered User</span>
              )}
            </div>
            <div>
              <span className="text-sm text-theme-text-secondary">Type:</span>
              <p className="text-theme-text capitalize">
                {submission.messageType.replace('_', ' ')}
              </p>
            </div>
            <div>
              <span className="text-sm text-theme-text-secondary">Message:</span>
              <p className="text-theme-text whitespace-pre-wrap">{submission.message}</p>
            </div>

            {/* Status Update Section */}
            <div className="pt-4 border-t border-theme-border">
              <span className="text-sm text-theme-text-secondary block mb-3">Update Status:</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleStatusChange('new')}
                  disabled={loading || selectedStatus === 'new'}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors font-medium text-sm ${
                    selectedStatus === 'new'
                      ? getStatusColor('new')
                      : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:border-blue-500/30 hover:text-blue-400'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  New
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusChange('in_progress')}
                  disabled={loading || selectedStatus === 'in_progress'}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors font-medium text-sm ${
                    selectedStatus === 'in_progress'
                      ? getStatusColor('in_progress')
                      : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:border-yellow-500/30 hover:text-yellow-400'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  In Progress
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusChange('resolved')}
                  disabled={loading || selectedStatus === 'resolved'}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors font-medium text-sm ${
                    selectedStatus === 'resolved'
                      ? getStatusColor('resolved')
                      : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:border-green-500/30 hover:text-green-400'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Resolved
                </button>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill-hover transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function SupportTab(): ReactElement {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [messageTypeFilter, setMessageTypeFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'registered' | 'public'>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionListItem | null>(null);

  const { submissions, total, pages, loading, error, refetch } = useContactSubmissions({
    page,
    limit: 50,
    search,
    status: statusFilter,
    messageType: messageTypeFilter,
    userType: userTypeFilter,
  });

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'new':
        return 'bg-blue-500/20 text-blue-400';
      case 'in_progress':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'resolved':
        return 'bg-green-500/20 text-green-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'issue':
        return AlertTriangle;
      case 'comment':
        return MessageSquare;
      case 'feature_request':
        return Send;
      case 'question':
        return Mail;
      default:
        return MessageSquare;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-theme-text">Support Center</h2>
        <p className="text-sm text-theme-text-secondary">
          Manage contact form submissions and user inquiries
        </p>
        <p className="text-sm text-theme-text-secondary">
          <span className="font-semibold text-theme-text">{total}</span> total submissions
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col @3xl:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-text-tertiary" />
          <input
            type="text"
            placeholder="Search by name, email, or message..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>

        <select
          value={messageTypeFilter}
          onChange={(e) => {
            setMessageTypeFilter(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text"
        >
          <option value="">All Types</option>
          <option value="issue">Issue</option>
          <option value="comment">Comment</option>
          <option value="feature_request">Feature Request</option>
          <option value="question">Question</option>
        </select>

        <select
          value={userTypeFilter}
          onChange={(e) => {
            setUserTypeFilter(e.target.value as 'all' | 'registered' | 'public');
            setPage(1);
          }}
          className="px-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text"
        >
          <option value="all">All Users</option>
          <option value="registered">Registered</option>
          <option value="public">Public</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger">
          {error}
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          <div className="bg-theme-surface rounded-xl border border-theme-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-theme-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    From
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    Type
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    Message
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {submissions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-theme-text-secondary">
                      No submissions found
                    </td>
                  </tr>
                ) : (
                  submissions.map((submission) => {
                    const TypeIcon = getMessageTypeIcon(submission.messageType);
                    return (
                      <tr
                        key={submission.id}
                        className="border-b border-theme-border hover:bg-theme-bg-secondary transition-colors cursor-pointer"
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {submission.isRegisteredUser ? (
                              <User className="w-4 h-4 text-theme-primary" />
                            ) : (
                              <Users className="w-4 h-4 text-theme-text-tertiary" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-theme-text">
                                {submission.name}
                              </p>
                              <p className="text-xs text-theme-text-tertiary">{submission.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-theme-text-secondary" />
                            <span className="text-sm text-theme-text capitalize">
                              {submission.messageType.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-theme-text truncate max-w-md">
                            {submission.message}
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(submission.status)}`}
                          >
                            {submission.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-theme-text-secondary">
                          {formatDate(submission.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-theme-text-secondary">
                Page {page} of {pages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPage(page + 1)}
                  disabled={page === pages}
                  className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onUpdate={() => {
            refetch();
            setSelectedSubmission(null);
          }}
        />
      )}
    </div>
  );
}
